import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@babackapp/customer-ui/components/input-group";
import { Separator } from "@babackapp/customer-ui/components/separator";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import { ArrowLeft } from "lucide-react";
import { motion } from "motion/react";
import { Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { useAddressForm } from "../hooks/use-address-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";

export function ReturnWidgetAddressCheck() {
  const { form, onSubmit, userPortalTexts } = useAddressForm();
  const t = useCustomText();

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("addressCheck.title")}</HeaderTitle>
        <HeaderDescription>
          {t("addressCheck.description")}
        </HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />

      <form onSubmit={onSubmit}>
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.1 } }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-6 gap-2"
        >
          <Controller
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput placeholder={t("common.fields.firstName")} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.firstName")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput placeholder={t("common.fields.lastNameShort")} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.lastName")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />

          <Separator className="col-span-6 my-4" />

          <Controller
            control={form.control}
            name="address1"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput placeholder={t("common.fields.address1")} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.address1")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="address2"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput placeholder={t("common.fields.address2Placeholder")} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.address2")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="city"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-2">
                <InputGroupInput placeholder={t("common.fields.city")} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.city")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="zip"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-3 col-span-2">
                <InputGroupInput placeholder={t("common.fields.zip")} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.zip")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="country_code"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-5 col-span-2">
                <InputGroupInput placeholder={t("common.fields.country")} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.country")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />

          <Separator className="col-span-6 my-4" />

          <Controller
            control={form.control}
            name="email"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput placeholder={t("common.fields.email")} type="email" {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.email")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="phone"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput placeholder="+33 6 00 00 00 00" type="tel" {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.phone")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
        </motion.div>

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Link to="../return-mode">
              <Button variant="secondary" type="button">
                <ArrowLeft />
                {t("common.back")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button type="submit">
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
